import { type ComponentType } from "react";
import { useTheme } from "../../contexts/theme-context";
import type { StepKey } from "../../components/step-progress/step-progress";
import { useAuth } from "../../contexts/auth/auth-context";
import { trackEvent } from "../../utils/analytics";

/* ───────────────── types ───────────────── */
export interface OnboardingStepProps {
    goNext: () => void;
    goPrev: () => void;
    goTo: (index: number) => void;
    stepIndex: number;
    showLogo?: boolean;
}

/** Steps can expose flags that influence the shell */
export type StepComponent = ComponentType<OnboardingStepProps> & {
    background?: string; // wrapper background
    fill?: boolean; // auto-height instead of 100 vh
    progress?: false | StepKey; // progress-bar phase or hide
    smallLogo?: boolean; // use 14-px logo instead of 20-px
    showLogo?: boolean; // show or hide shell logo
    containerAlign?: string; // flex align-items for container
    containerJustify?: string; // flex justify-content for container
};

export interface RegistrationShellProps {
    steps: StepComponent[];
}

/* ───────────────── hook ───────────────── */
export const useRegistrationShell = (steps: StepComponent[]) => {
    const { isLight } = useTheme();
    const { currentStep, setCurrentStep } = useAuth();

    // Clamp currentStep to valid range
    const index = Math.max(0, Math.min(currentStep ?? 0, steps.length - 1));

    /* navigation helpers (write back into context) */
    const goNext = () => {
        trackEvent('registration_step_completed', {
            step_index: index,
            step_name: steps[index]?.displayName ?? `step_${index}`,
        });
        setCurrentStep(Math.min(index + 1, steps.length - 1));
    };

    const goPrev = () => setCurrentStep(Math.max(index - 1, 0));

    const goTo = (i: number) =>
        setCurrentStep(Math.max(0, Math.min(i, steps.length - 1)));

    const Step = steps[index];
    const background = Step.background ?? "#1A1A1C";
    const fill = Step.fill ?? false;
    const progressKey = Step.progress;
    const smallLogo = Step.smallLogo ?? false;
    const showLogo = Step.showLogo ?? true;
    const containerAlign = Step.containerAlign ?? "center";
    const containerJustify = Step.containerJustify ?? "center";

    return {
        isLight,
        index,
        goNext,
        goPrev,
        goTo,
        Step,
        background,
        fill,
        progressKey,
        smallLogo,
        showLogo,
        containerAlign,
        containerJustify,
    };
};
